// Why this file exists
//
// Every JavaScript file the browser downloads from us ("chunk") is named after
// a hash of its contents, e.g. `3pf4oto09ifkg.js`, and served with a one-year
// cache header. Same contents, same name, so a returning visitor's browser
// reuses its saved copy instead of downloading the file again. Change one
// byte and the file gets a new name, so every visitor downloads it again.
//
// Two values differ on every deploy even when no code does: the git commit
// SHA, and the Sentry "release" name (also the SHA on Vercel), which tells
// Sentry which deploy an error came from. If either is written into a chunk,
// that chunk is renamed and re-downloaded after every deploy. On the sessions
// page that would be five chunks, 187 KB compressed, 10% of its JavaScript.
//
// So the values never go into a chunk. They travel in the HTML page instead,
// which is fetched fresh on every visit anyway:
//
//   1. Build: next.config.js hands both values to server code only.
//   2. Server: the page layouts (app/layout.tsx, pages/_document.tsx) put
//      `buildInfoBootstrap()`'s output in a <script> at the top of <head>, so
//      the page contains e.g. `window.__BUILD_SHA__="ab12…";`.
//   3. Browser: that script runs before any chunk, and browser code reads the
//      values back with `buildSha()` and `sentryRelease()`.

import * as z from "zod";

// `unknown`, not `string`: the page or a browser extension could have put
// anything on `window`, so readers check the value before trusting it.
declare global {
  interface Window {
    __BUILD_SHA__?: unknown;
    __SENTRY_RELEASE__?: unknown;
  }
}

/**
 * The inline script's source, e.g. `window.__BUILD_SHA__="ab12…";window.__SENTRY_RELEASE__="ab12…";`.
 * Server-only: `process.env.BUILD_SHA` and `process.env._sentryRelease` are
 * filled in only in server code (see `defineServer` in next.config.js), and
 * read as undefined in the browser.
 */
export function buildInfoBootstrap(): string {
  const values = {
    __BUILD_SHA__: process.env.BUILD_SHA,
    __SENTRY_RELEASE__: process.env._sentryRelease,
  };
  return (
    Object.entries(values)
      .map(([key, value]) => `window.${key}=${JSON.stringify(value ?? null)};`)
      .join("")
      // A value containing "</script>" would end the script tag early and
      // turn the rest into HTML. `\u003c` is the same "<" to JavaScript but
      // not to the HTML parser.
      .replaceAll("<", "\\u003c")
  );
}

// Anything that is not a string (missing, null, tampered with) reads as null.
const WindowString = z.string().nullable().catch(null);

function windowString(value: unknown): string | null {
  return WindowString.parse(value);
}

// Both readers return nothing during server rendering, where there is no
// `window`; their callers only use them in the browser.

/** The deployed commit SHA, or "dev" for builds outside Vercel. */
export function buildSha(): string | null {
  return typeof window === "undefined"
    ? null
    : windowString(window.__BUILD_SHA__);
}

/**
 * The release name Sentry's build step registered and uploaded source maps
 * under. Errors must carry the same name, or Sentry cannot match them to the
 * deploy and its source maps.
 */
export function sentryRelease(): string | undefined {
  return typeof window === "undefined"
    ? undefined
    : (windowString(window.__SENTRY_RELEASE__) ?? undefined);
}
